// R5 (hook) — hard alignment. Mirrors the reference mockup's
// `applyAlignment()` (docs/mockups/side-by-side.html): walk the anchors
// top-down and, for each one, push whichever pane's anchor sits higher down by
// the delta via `marginTop`, so paired anchors end at equal heights and the
// panes become height-identical for a locked 1:1 scroll.
//
// This is the reader's DEFAULT mode as well as the editor's: `reading-view.tsx`
// resolves it as `getPref("syncMode", "hard")`, and only `?sync=interpolated`
// turns it off (SPEC "Sync anchors").
//
// This is deliberately NOT computed from a single static measurement of
// segment heights (`buildSyncSegments`/`computeHardAlignPadding`, still used
// by `useSyncScroll` for interpolation and kept exported for the phase-7
// anchor editor): margins injected by earlier anchors shift every later block,
// so each correction has to observe the previous ones. Sub-block anchors snap
// to their containing block's top — whitespace can't be inserted mid-paragraph
// (interpolated mode honors them exactly) — and a block drives/receives at most
// ONE correction: consecutive anchors resolving to the same containing block on
// either side (sub-block anchors sharing one paragraph) are skipped, since
// re-correcting the shared block would just compound margins onto it and shove
// the whole pane down by the sum of every delta.
//
// The margin injection is a legitimate layout side-effect — it mutates style on
// the rendered block elements via refs and fully resets them on cleanup /
// re-run.
//
// ## Why the measure pass is batched, and why that is the same answer
//
// The mockup makes each correction observe the previous ones the direct way:
// it re-measures LIVE inside the write loop, one `getBoundingClientRect` per
// anchor after every margin write. That is a forced layout per anchor — 4 rect
// reads each over the corpus's 35–104 anchors per chapter, three passes on
// two-pane entry (mount, `document.fonts.ready`, the ResizeObserver rAF) — and
// it measured as the reader's one real hotspot: 265ms of rect-read time across
// 1266 calls on a chapter-4 pane toggle at 6x CPU throttle, one 331ms frame
// carrying 174ms of forcedStyleAndLayout inside the click handler.
//
// UNVERIFIED IN THIS REPO, check before relying on it: the anchor range and
// the profile numbers above come from the 2026-08-22 production triage, which
// is not committed here, and no anchor snapshot ships in this tree to
// re-derive the range from (`find . -name '*anchor*.json'` outside
// node_modules finds nothing). They are recorded because they are why this
// function has the shape it has, not because anything here can check them.
// This file is the single site for that range — block-action-bar.tsx used to
// carry its own copy, which had drifted to 35–93.
// `runHardAlignPass` below keeps the semantics and drops the interleave, by
// reading every anchor, then writing every margin, then re-reading to check.
//
// Both panes' blocks are direct children of one `flex flex-col` article
// (document-pane.tsx), whose items' margins never collapse, and blocks carry
// only `mb-*`/`my-0` — so injected inline `marginTop` starts from a computed 0
// and contributes exactly its own px. A margin written on an element therefore
// shifts that element and every LATER sibling down by precisely that much, and
// shifts nothing earlier. Anchors are strictly monotonic by construction
// (docs/anchor-algorithm.md), so anchor k's two elements are strictly after
// anchor j's for every j < k.
//
// Under that premise the sweep is closed-form. Writing a correction d_j at
// anchor j moves the live delta (retelling top − original top) at every LATER
// anchor by exactly −d_j: pushing the original down subtracts, pushing the
// retelling down adds, and both branches write |d_j|. So with D_k the NATURAL
// delta at anchor k (all margins cleared), the interleaved sweep computes
//
//     d_k = D_k − Σ_{j<k} d_j
//
// and by induction that telescopes to d_k = D_k − D_{k−1}: every correction is
// just the increment of the natural delta, and the accumulated correction
// entering anchor k is always exactly −D_{k−1}. The batched pass computes the
// identical numbers from ONE natural snapshot by carrying that running sum
// itself (`carry` in `applyRound`) instead of asking the layout engine to
// re-derive it through a reflow. Same writes, same margins, same final
// alignment — at TWO forced layouts per pass (the round-0 measure and the
// round-1 confirm below) instead of one per anchor.
//
// The rounds are the safety net for that premise, not a second mechanism. If a
// write's effect is ever NOT a pure downward shift of exactly its own px — a
// margin that collapses after all, an absolutely positioned block, anchor data
// that is not really monotonic — the next round MEASURES the residual and
// corrects it, up to `MAX_ALIGN_ROUNDS`. Under the CSS above round 1 measures
// zero and only confirms, so the loop never reaches round 2. Where the premise
// does fail, the batched pass converges on alignment the interleaved sweep
// could not: the sweep visits each anchor once and has no way to notice that a
// correction did not land the way it asked.

import { useLayoutEffect, useRef, type RefObject } from "react";
import type { CorpusAnchorPair } from "@bindery/shared/corpus/anchors";
import type { DocumentModel } from "@bindery/shared/corpus/document";

const ECHO_EPSILON = 1.5;

/**
 * A residual smaller than this is layout rounding, not misalignment: browsers
 * report fractional rects and honor fractional margins only to ~1/64px, so
 * even an exactly-converged pass measures a hair off zero. Half a CSS pixel is
 * far below anything a reader can see and far above that noise.
 */
const ALIGN_EPSILON = 0.5;

/**
 * Ceiling on measure→write rounds in one pass. A healthy layout never reaches
 * it (round 0 is exact and round 1 confirms — see the header note); the bound
 * exists so a layout that breaks the additivity premise degrades into a few
 * extra rounds rather than an unbounded loop.
 */
const MAX_ALIGN_ROUNDS = 3;

/**
 * Reset any injected margins so the next measure sees natural layout. Scoped
 * to the article's direct children (the blocks themselves): every text run
 * span also carries `data-start`, so an unscoped `[data-start]` selector would
 * sweep hundreds–thousands of run spans on every measure (each ResizeObserver
 * tick / font event) for no reason — only block-level elements ever get a
 * margin injected.
 */
function clearMargins(container: HTMLElement): void {
  const els = container.querySelectorAll<HTMLElement>(
    ":scope > article > [data-start]",
  );
  for (const el of els) {
    el.style.marginTop = "";
    el.style.marginBottom = "";
  }
}

/** Precise selector for a block's rendered element (runs also carry data-start,
 *  so match the id when present and the exact start otherwise). */
function blockEl(
  container: HTMLElement,
  block: { id?: string; start: number },
): HTMLElement | null {
  const selector = block.id
    ? `[data-block-id="${block.id}"]`
    : `[data-start="${String(block.start)}"]`;
  return container.querySelector<HTMLElement>(selector);
}

/** DOM element that owns an original char offset (snap to containing block). */
function originalBlockEl(
  container: HTMLElement,
  model: DocumentModel,
  offset: number,
): HTMLElement | null {
  const block =
    model.blocks.find((b) => offset < b.end) ??
    model.blocks[model.blocks.length - 1];
  return block ? blockEl(container, block) : null;
}

/** Grow an injected margin. Only ever ADDS, which is what keeps a pass from
 *  shrinking content under a live `scrollTop` (see `readPaneFrame`). */
function addMargin(el: HTMLElement, px: number, bottom = false): void {
  if (px <= 0) return;
  const prop = bottom ? "marginBottom" : "marginTop";
  const current = parseFloat(el.style[prop] || "0");
  el.style[prop] = `${String(current + px)}px`;
}

/** One anchor resolved to the two block elements it corrects. */
interface AnchorEls {
  readonly retEl: HTMLElement;
  readonly origEl: HTMLElement;
}

/**
 * Element identity for every anchor that will actually be corrected — resolved
 * ONCE per pass, since nothing in the pass mutates the DOM tree, only styles.
 * Carries the mockup's two skips verbatim: a stale anchor whose block is gone,
 * and the same-block guard (anchors are monotonic, so same-block repeats are
 * consecutive and the previous corrected pair suffices; verse originals are
 * per-line blocks — scripts/ingest/verseLines.ts — so this no longer suppresses
 * verse corrections).
 */
function resolveAnchorEls(
  retelling: HTMLElement,
  original: HTMLElement,
  originalModel: DocumentModel,
  anchors: readonly CorpusAnchorPair[],
): AnchorEls[] {
  const resolved: AnchorEls[] = [];
  let prevRetEl: HTMLElement | null = null;
  let prevOrigEl: HTMLElement | null = null;
  for (const pair of anchors) {
    const retEl = retelling.querySelector<HTMLElement>(
      `[data-block-id="${pair.retelling.blockId}"]`,
    );
    if (!retEl) continue; // stale anchor — retelling block no longer exists
    const origEl = originalBlockEl(
      original,
      originalModel,
      pair.originalOffset,
    );
    if (!origEl) continue;
    if (retEl === prevRetEl || origEl === prevOrigEl) continue;
    resolved.push({ retEl, origEl });
    prevRetEl = retEl;
    prevOrigEl = origEl;
  }
  return resolved;
}

/** A pane's scroll frame: what turns a viewport rect into a content-space top. */
interface PaneFrame {
  readonly rectTop: number;
  readonly scrollTop: number;
}

/**
 * Read a pane's frame once per round. Safe to hoist out of the measure loop
 * even though the mockup re-read it per anchor: the pane is a fixed-height
 * `overflow-y-auto` box (document-pane.tsx), so margins injected into its
 * scroll CONTENT cannot move its own border box, and `addMargin` only grows
 * margins within a pass, so the content can only get taller — nothing mid-pass
 * can clamp `scrollTop`. (The one shrink, `clearMargins`, happens before the
 * first frame read, so any clamp it causes is already in this value.)
 */
function readPaneFrame(pane: HTMLElement): PaneFrame {
  return {
    rectTop: pane.getBoundingClientRect().top,
    scrollTop: pane.scrollTop,
  };
}

/** Live top of an element within its pane's scroll content — the mockup's
 *  `topIn`, minus the per-call pane rect read. */
function topIn(frame: PaneFrame, el: HTMLElement): number {
  return el.getBoundingClientRect().top - frame.rectTop + frame.scrollTop;
}

/** One anchor's measured misalignment: retelling top − original top. */
interface MeasuredAnchor {
  readonly els: AnchorEls;
  readonly delta: number;
}

/**
 * READ phase. Every rect read in one uninterrupted batch — no style write
 * between them, so the browser lays out once and every read after that is a
 * cache hit.
 */
function measureAnchors(
  anchorEls: readonly AnchorEls[],
  retFrame: PaneFrame,
  origFrame: PaneFrame,
): MeasuredAnchor[] {
  return anchorEls.map((els) => ({
    els,
    delta: topIn(retFrame, els.retEl) - topIn(origFrame, els.origEl),
  }));
}

/**
 * WRITE phase. Top-down over the same snapshot, pushing whichever pane's
 * anchor is higher down to match — with `carry` standing in for the reflow the
 * mockup used to buy here, since the effect of this round's earlier writes on
 * this anchor is known exactly (see the header note): each write of `delta`
 * moves every later anchor's delta by `−delta`.
 */
function applyRound(measured: readonly MeasuredAnchor[]): void {
  let carry = 0;
  for (const { els, delta: measuredDelta } of measured) {
    const delta = measuredDelta + carry;
    if (delta > 0) addMargin(els.origEl, delta); // original higher → push it down
    else if (delta < 0) addMargin(els.retEl, -delta);
    carry -= delta;
  }
}

/**
 * Tail sentinel: equalize the remaining content past the last anchor so the
 * panes become height-identical end-to-end — needed for the 1:1 scroll lock
 * (the mockup itself has no lock-critical trailing content to equalize; the
 * reader does). Runs once, after the rounds, on the settled layout.
 */
function equalizeTails(
  retelling: HTMLElement,
  original: HTMLElement,
  retellingModel: DocumentModel,
  originalModel: DocumentModel,
): void {
  // Index, not `.at(-1)` (past the chrome80/safari14 BROWSER_FLOOR).
  const lastRetBlock = retellingModel.blocks[retellingModel.blocks.length - 1];
  const lastOrigBlock = originalModel.blocks[originalModel.blocks.length - 1];
  const lastRetEl = lastRetBlock ? blockEl(retelling, lastRetBlock) : null;
  const lastOrigEl = lastOrigBlock ? blockEl(original, lastOrigBlock) : null;
  if (!lastRetEl || !lastOrigEl) return;

  const retFrame = readPaneFrame(retelling);
  const origFrame = readPaneFrame(original);
  const retRect = lastRetEl.getBoundingClientRect();
  const origRect = lastOrigEl.getBoundingClientRect();
  const retBottom =
    retRect.top - retFrame.rectTop + retFrame.scrollTop + retRect.height;
  const origBottom =
    origRect.top - origFrame.rectTop + origFrame.scrollTop + origRect.height;
  const delta = retBottom - origBottom;
  if (delta > 0) addMargin(lastOrigEl, delta, true);
  else if (delta < 0) addMargin(lastRetEl, -delta, true);
}

/**
 * One full alignment pass: clear, then rounds of read-all-then-write-all until
 * every anchor measures aligned (or `MAX_ALIGN_ROUNDS` is spent), then the tail
 * sentinel. Exported for its spec — the hook is the only production caller.
 */
export function runHardAlignPass(args: {
  retelling: HTMLElement;
  original: HTMLElement;
  retellingModel: DocumentModel;
  originalModel: DocumentModel;
  anchors: readonly CorpusAnchorPair[];
}): void {
  const { retelling, original, retellingModel, originalModel, anchors } = args;
  clearMargins(retelling);
  clearMargins(original);

  const anchorEls = resolveAnchorEls(
    retelling,
    original,
    originalModel,
    anchors,
  );

  for (let round = 0; round < MAX_ALIGN_ROUNDS; round += 1) {
    const measured = measureAnchors(
      anchorEls,
      readPaneFrame(retelling),
      readPaneFrame(original),
    );
    // Round 0 measures the natural layout and always writes, so that its
    // margins are exactly the sweep's (a sub-epsilon natural delta still gets
    // its sub-epsilon margin). From round 1 the read is a CHECK: nothing left
    // to correct means the previous round's writes landed as computed, which
    // is the common case and the reason this costs two forced layouts.
    if (round > 0 && measured.every((m) => Math.abs(m.delta) <= ALIGN_EPSILON))
      break;
    applyRound(measured);
  }

  equalizeTails(retelling, original, retellingModel, originalModel);
}

interface UseHardAlignArgs {
  retellingRef: RefObject<HTMLDivElement | null>;
  originalRef: RefObject<HTMLDivElement | null>;
  retellingModel: DocumentModel;
  originalModel: DocumentModel | null;
  anchors: readonly CorpusAnchorPair[];
  /** Both panes visible AND hard mode. */
  enabled: boolean;
  retellingFontSize: number;
  originalFontSize: number;
  /** Opaque token; changing it re-runs the pass (see useSyncScroll). */
  layoutKey: string;
}

export function useHardAlign({
  retellingRef,
  originalRef,
  retellingModel,
  originalModel,
  anchors,
  enabled,
  retellingFontSize,
  originalFontSize,
  layoutKey,
}: UseHardAlignArgs): void {
  const expectedRef = useRef(new Map<HTMLElement, number>());

  useLayoutEffect(() => {
    const retelling = retellingRef.current;
    const original = originalRef.current;
    if (!enabled || !retelling || !original || !originalModel) return;

    // Stable useRef Map; local alias so cleanup closes over it (react-hooks lint).
    const expectedTops = expectedRef.current;

    const applyPadding = (): void => {
      runHardAlignPass({
        retelling,
        original,
        retellingModel,
        originalModel,
        anchors,
      });
    };

    // 1:1 scroll lock — heights match after equalization. `behavior:
    // "instant"` overrides CSS `scroll-behavior`, so the follower's set
    // produces exactly one scroll event (see use-sync-scroll's syncFrom).
    // Clamp before recording: padded heights only equalize up to layout
    // rounding, so near the document bottom an unclamped assignment gets
    // clamped by the browser anyway, and the echoed event would then differ
    // from the recorded (unclamped) value enough to misread as a genuine
    // gesture and bounce the leader back.
    const mirror = (fromEl: HTMLElement, toEl: HTMLElement): void => {
      const maxScroll = toEl.scrollHeight - toEl.clientHeight;
      const next = Math.max(0, Math.min(fromEl.scrollTop, maxScroll));
      // Skip recording a no-op: it fires no echo to clear the entry, and a
      // stale one would swallow the follower's next genuine gesture event.
      if (Math.abs(toEl.scrollTop - next) > ECHO_EPSILON) {
        expectedTops.set(toEl, next);
      }
      toEl.scrollTo({ top: next, behavior: "instant" });
    };
    const onScroll = (ev: Event): void => {
      const el = ev.currentTarget as HTMLElement;
      const expected = expectedTops.get(el);
      if (
        expected !== undefined &&
        Math.abs(el.scrollTop - expected) <= ECHO_EPSILON
      ) {
        expectedTops.delete(el);
        return;
      }
      expectedTops.delete(el);
      mirror(el, el === retelling ? original : retelling);
    };

    applyPadding();
    retelling.addEventListener("scroll", onScroll, { passive: true });
    original.addEventListener("scroll", onScroll, { passive: true });

    // One pass per FRAME, not one per observation. A drag-resize of the window
    // fires this observer for both panes on every frame it moves, and a pass
    // is still two forced layouts plus a rect read per anchor. Cancel-and-
    // reschedule keeps the LAST observation of each frame, which is the only
    // one whose measurements are still true.
    let rafId: number | null = null;
    const observer = new ResizeObserver(() => {
      if (rafId !== null) window.cancelAnimationFrame(rafId);
      rafId = window.requestAnimationFrame(() => {
        rafId = null;
        applyPadding();
      });
    });
    observer.observe(retelling);
    observer.observe(original);
    document.fonts?.ready.then(applyPadding).catch(() => undefined);

    return () => {
      retelling.removeEventListener("scroll", onScroll);
      original.removeEventListener("scroll", onScroll);
      observer.disconnect();
      if (rafId !== null) window.cancelAnimationFrame(rafId);
      clearMargins(retelling);
      clearMargins(original);
      expectedTops.clear();
    };
  }, [
    retellingRef,
    originalRef,
    retellingModel,
    originalModel,
    anchors,
    enabled,
    retellingFontSize,
    originalFontSize,
    layoutKey,
  ]);
}
